import type { ToolCallEntity } from '@platform/timeline';
import type { FetchDocumentContentResponse } from '@shared/types/drive';
import { parseMaybeDoubleEncodedJSON } from '../parseMaybeDoubleEncodedJSON';
import { registerSem } from '../registry';
import { firstNonEmptyString as firstString } from '../strings';
import { toolLabelProviderFor } from '../toolLabels';

type AnyMap = Record<string, unknown>;
type ToolTerminalStatus = 'completed' | 'error';

const toolTerminalStatusById = new Map<string, ToolTerminalStatus>();

function getData(ev: AnyMap): AnyMap {
  const d = (ev as any).data as AnyMap | undefined;
  return d ?? {};
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

function getName(ev: AnyMap): string {
  // Prefer explicit name; fall back to tool_name on result events
  return String((ev as any).name ?? (ev as any).tool_name ?? 'tool');
}

function getInput(ev: AnyMap): Record<string, unknown> | undefined {
  const d = getData(ev);
  const top = (ev as any).input;
  return asRecord(d?.input) ?? asRecord(top);
}

function getPatch(ev: AnyMap): Record<string, unknown> | undefined {
  return asRecord((ev as any).patch);
}

registerSem('tool.start', ({ ev, now, convId }) => {
  const id = (ev as any).id as string;
  if (!id) return null;
  toolTerminalStatusById.delete(id);
  const name = getName(ev as any);
  const input = getInput(ev as any);
  const d = getData(ev as any);
  const provider = toolLabelProviderFor(name);
  const fromEv = {
    label: (d as any)?.label as string | undefined,
    argsSummary: (d as any)?.args_summary as string | undefined,
  };
  const fromProvider = provider.labelForStart(name, input);
  const label = fromEv.label ?? fromProvider.label;
  const argsSummary = fromEv.argsSummary ?? fromProvider.argsSummary;
  const details = (d as any)?.details as any[] | undefined;
  const entity: ToolCallEntity = {
    id,
    kind: 'tool_call',
    timestamp: now,
    props: {
      name,
      input,
      status: 'running',
      progress: 0,
      label,
      argsSummary,
      details,
    } as any,
  };
  return { kind: 'add', convId, entity };
});

registerSem('tool.delta', ({ ev, now, convId }) => {
  const id = (ev as any).id as string;
  if (!id) return null;
  const patch = getPatch(ev as any);
  const progress =
    typeof (patch as any)?.progress === 'number' ? ((patch as any).progress as number) : undefined;
  const entity: ToolCallEntity = {
    id,
    kind: 'tool_call',
    timestamp: now,
    props: {
      status: 'running',
      progress: progress ?? 0,
    } as any,
  };
  return { kind: 'upsert', convId, entity };
});

registerSem('tool.result', ({ ev, now, convId }) => {
  const id = (ev as any).id as string;
  if (!id) return null;
  const name = getName(ev as any);
  const result = (ev as any).result as unknown;
  const parsedResult = parseMaybeDoubleEncodedJSON(result, { onParseError: 'original' });
  const d = getData(ev as any);
  const provider = toolLabelProviderFor(name);
  const fromEv = {
    resultSummary: (d as any)?.result_summary as string | undefined,
    resultCount: (d as any)?.result_count as number | undefined,
  };
  const details = (d as any)?.details as any[] | undefined;
  const fromProvider = provider.labelForResult(name, parsedResult);
  // Prefer provider-derived counts when they are higher or event provided is missing
  const chosenCount =
    typeof fromProvider.resultCount === 'number' &&
    (typeof fromEv.resultCount !== 'number' ||
      (fromProvider.resultCount as number) > (fromEv.resultCount as number))
      ? (fromProvider.resultCount as number)
      : (fromEv.resultCount as number | undefined);
  const resultCount = chosenCount ?? fromProvider.resultCount;
  const resultSummary =
    resultCount && resultCount > 0
      ? fromProvider.resultSummary || fromEv.resultSummary
      : fromEv.resultSummary || fromProvider.resultSummary;
  const entity: ToolCallEntity = {
    id,
    kind: 'tool_call',
    timestamp: now,
    props: {
      status: 'completed',
      progress: 1,
      resultSummary,
      resultCount,
      details,
    } as any,
  };
  // Extract Drive compression data for display widgets
  if (name === 'drive_fetch_document_content') {
    const r = asRecord(parsedResult) ?? {};
    const documentId =
      firstString(
        (r as any)?.documentId,
        (r as any)?.document_id,
        (r as any)?.doc_id,
        (r as any)?.file_id,
        (r as any)?.id
      ) ?? '';
    const content = firstString((r as any)?.content, (r as any)?.text) ?? '';
    const markdown = firstString((r as any)?.markdown) ?? '';
    const status = firstString((r as any)?.status);
    const reasonCode = firstString((r as any)?.reasonCode, (r as any)?.reason_code);
    const userMessage = firstString((r as any)?.userMessage, (r as any)?.user_message);
    const docName = firstString((r as any)?.name, (r as any)?.title);
    const mimeType = firstString((r as any)?.mimeType, (r as any)?.mime_type);
    const summary = firstString((r as any)?.summary);
    const webViewUrl = firstString(
      (r as any)?.webViewUrl,
      (r as any)?.web_view_url,
      (r as any)?.web_view_link,
      (r as any)?.url,
      (r as any)?.link
    );
    // Only attach driveData if we have something meaningful to display; otherwise keep generic tool UI.
    // Note: documentId alone is not user-facing (we intentionally hide IDs in the UI).
    if (
      content ||
      markdown ||
      summary ||
      docName ||
      mimeType ||
      webViewUrl ||
      status ||
      reasonCode ||
      userMessage
    ) {
      const driveData: FetchDocumentContentResponse = {
        documentId,
        status: status as FetchDocumentContentResponse['status'],
        reasonCode: reasonCode as FetchDocumentContentResponse['reasonCode'],
        userMessage,
        content,
        markdown,
        name: docName,
        mimeType,
        summary,
        webViewUrl,
      };
      (entity.props as any).driveData = driveData;
    }
    if (status === 'content_unavailable') {
      (entity.props as any).status = 'error';
      (entity.props as any).error = userMessage || 'Could not read this document content.';
    }
  }
  const terminalStatus = (
    (entity.props as any)?.status === 'error' ? 'error' : 'completed'
  ) as ToolTerminalStatus;
  toolTerminalStatusById.set(id, terminalStatus);
  return { kind: 'upsert', convId, entity };
});

registerSem('tool.done', ({ ev, now, convId }) => {
  const id = (ev as any).id as string;
  if (!id) return null;
  const terminalStatus = toolTerminalStatusById.get(id);
  toolTerminalStatusById.delete(id);
  const shouldSetCompletedStatus = terminalStatus !== 'error';
  const entity: ToolCallEntity = {
    id,
    kind: 'tool_call',
    timestamp: now,
    props: {
      ...(shouldSetCompletedStatus ? { status: 'completed' } : {}),
      progress: 1,
    } as any,
  };
  return { kind: 'upsert', convId, entity };
});
